import { useTranslations } from "next-intl"
import { onboardingRoutesPaths } from "@/routes/onboarding"
import { storyblokRoutesPaths } from "@/routes/storyblok"
import { type MenuItem } from "../types/menuItem"
import { useNavigationContent } from "./useNavigationContent"

type Translate = ReturnType<typeof useTranslations<"useNavigationMenu">>

/**
 * Hardcoded menu, used until Storyblok answers and if it ever fails.
 * Keep in sync with the `globalHeader` story content.
 */
function getDefaultNavigationMenu(t: Translate): MenuItem[] {
    return [
        {
            label: t("mainLink.energyOffers"),
            href: storyblokRoutesPaths.tariffs.resolve(),
            key: "home",
            subMenu: [
                {
                    label: t("subLink.onboarding"),
                    href: onboardingRoutesPaths.meterDetails.resolve(),
                    key: "onboarding",
                },
                {
                    label: t("subLink.tariffs"),
                    href: storyblokRoutesPaths.tariffs.resolve(),
                    key: "tariffs",
                },
                {
                    label: t("subLink.faq"),
                    href: storyblokRoutesPaths.faq.resolve(),
                    key: "faq",
                },
                {
                    label: t("subLink.contact"),
                    href: storyblokRoutesPaths.landing.resolve({
                        pathParams: { landingId: "contact" },
                    }),
                    key: "contact",
                },
            ],
        },
        {
            label: t("mainLink.electricVehicles"),
            href: storyblokRoutesPaths.electricVehicles.resolve(),
            key: "electricVehicles",
        },
        {
            label: t("mainLink.equipments"),
            href: storyblokRoutesPaths.home.resolve(),
            key: "equipments",
            subMenu: [
                {
                    label: t("subLink.heatPumps"),
                    href: storyblokRoutesPaths.landing.resolve({
                        pathParams: {
                            landingId: "pompe-a-chaleur",
                        },
                    }),
                    key: "heatPumps",
                },
                {
                    label: t("subLink.storageBattery"),
                    href: storyblokRoutesPaths.landing.resolve({
                        pathParams: {
                            landingId: "nook-batterie-de-stockage",
                        },
                    }),
                    key: "storageBattery",
                },
                {
                    label: t("subLink.controlledWaterHeaters"),
                    href: storyblokRoutesPaths.landing.resolve({
                        pathParams: {
                            landingId: "ballons",
                        },
                    }),
                    key: "controlledWaterHeaters",
                },
            ],
        },
        {
            label: t("mainLink.solarPanels"),
            href: storyblokRoutesPaths.solarPanels.resolve(),
            key: "solarPanels",
        },
        {
            label: t("mainLink.chooseOctopus"),
            href: storyblokRoutesPaths.landing.resolve({
                pathParams: { landingId: "qui-sommes-nous" },
            }),
            key: "chooseOctopus",
            subMenu: [
                {
                    label: t("subLink.about"),
                    href: storyblokRoutesPaths.landing.resolve({
                        pathParams: { landingId: "qui-sommes-nous" },
                    }),
                    key: "about",
                },
                {
                    label: t("subLink.blog"),
                    href: storyblokRoutesPaths.blog.resolve(),
                    key: "blog",
                },
                {
                    label: t("subLink.greenEnergy"),
                    href: storyblokRoutesPaths.greenEnergy.resolve(),
                    key: "greenEnergy",
                },
                {
                    label: t("subLink.fanClub"),
                    href: storyblokRoutesPaths.fanClub.resolve(),
                    key: "fanClub",
                },
                {
                    label: t("subLink.business"),
                    href: storyblokRoutesPaths.landing.resolve({
                        pathParams: {
                            landingId: "professionnels-entreprises",
                        },
                    }),
                    key: "business",
                },
                {
                    label: t("subLink.regional"),
                    href: storyblokRoutesPaths.regional.resolve(),
                    key: "regional",
                },
            ],
        },
    ]
}

/**
 * Header menu, driven by the `globalHeader` Storyblok story.
 *
 * Falls back to the hardcoded menu while the content is loading, or if
 * Storyblok is unreachable, so the header is never rendered empty.
 */
export function useNavigationMenu(): MenuItem[] {
    const t = useTranslations("useNavigationMenu")
    const { headerMenu } = useNavigationContent()

    return headerMenu.length ? headerMenu : getDefaultNavigationMenu(t)
}

useNavigationMenu.messages = ["useNavigationMenu"] as const
